import type {
  AssetComponentDto,
  AssetDetail,
  RenewalPaymentDto,
  SupplierRenewalDto,
} from '@fos/shared/contracts/maintenance';
import { poolUsageLine } from '@fos/shared/contracts/services';
import {
  ASSET_COMPONENT_KIND_LABELS,
  ASSET_STATUS_LABELS,
  ASSET_STATUS_TONES,
  ASSET_TYPE_SUBTITLES,
  describeReminderRule,
  RENEWAL_PAYMENT_STATUS_LABELS,
  RENEWAL_PAYMENT_STATUS_TONES,
} from '@fos/shared/labels';
import { Check, ExternalLink, Minus, Pencil, RefreshCw, Server } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { formatDate, formatMoneyAuto } from '../../../lib/format';
import { useRememberRecent } from '../../../lib/recent';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { ContextBanner } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Mono } from '../../../ui/Mono';
import { Page } from '../../../ui/Page';
import { Breadcrumbs } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { Dot } from '../../../ui/Status';
import { StatusPill } from '../../../ui/StatusPill';
import { toast } from '../../../ui/Toast';
import { ObjectHistory } from '../../history';
import { TOUCH_BUTTON, TOUCH_TEXT } from '../../mobile';
import { ModulePage, NotFoundPage } from '../../modules/pages/ModulePage';
import { AssetMonitoringCard, ServerMonitorsCard } from '../../monitoring';
import { ServiceFormDialog } from '../../services/components/ServiceFormDialog';
import { servicePriceText, serviceSinceText } from '../../services/service-format';
import { EditAssetDialog } from '../components/EditAssetDialog';
import { ComponentDialog, SupplierRenewalDialog } from '../components/ItemDialogs';
import { AssetTypeIcon, CardSectionHeader, SectionAction } from '../components/MaintenanceBits';
import { RemindersDialog } from '../components/RemindersDialog';
import { PoolBalance, PoolProgress } from '../components/ServicePool';
import { CredentialsSection } from '../components/vault';
import {
  amountWithCycle,
  assetHref,
  COMPONENT_ICONS,
  dateText,
  daysLeftText,
  locationText,
  renewalTone,
} from '../maintenance-format';

/**
 * Karta zasobu (Figma 08, 3044:3978): nagłówek z typem, statusem i „gdzie stoi”, baner płatności w ciągu 30 dni
 * (z informacją o mailu w Akceptacjach), lewa kolumna — ustalenia umowy, elementy, dostępy (sejf: `CredentialsSection`,
 * bez sejfu „Dostępy: ograniczone”), prawa — płatności (klient płaci / my płacimy / historia), przypomnienia,
 * monitoring (`AssetMonitoringCard` z modułu monitoring). Edycja tylko z modułem Utrzymanie = Edycja i zakresem
 * z edycją (`canEdit` z serwera).
 */
export function AssetDetailPage() {
  const access = useAccess();
  const { id } = useParams();
  if (!access.can('maintenance')) return <ModulePage module="maintenance" />;
  if (!id) return <NotFoundPage />;
  return <AssetDetailView id={id} />;
}

function AssetDetailView({ id }: { id: string }) {
  const query = trpc.maintenance.getAsset.useQuery({ id });

  if (query.isPending) {
    return (
      <Page>
        <div role="status" aria-label="Wczytywanie zasobu">
          <Skeleton className="h-4 w-40" />
          <div className="mt-4 flex items-center gap-3.5">
            <Skeleton className="size-11 rounded-xl" />
            <div className="flex flex-col gap-2">
              <Skeleton className="h-6 w-56" />
              <Skeleton className="h-4 w-80" />
            </div>
          </div>
          <div className="mt-8 grid gap-4 md:gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <Skeleton className="h-64 rounded-xl" />
            <Skeleton className="h-64 rounded-xl" />
          </div>
        </div>
      </Page>
    );
  }
  if (query.isError) {
    if (appErrorCode(query.error) === 'NOT_FOUND') {
      return (
        <Page>
          <Breadcrumbs items={[{ label: 'Utrzymanie', to: '/utrzymanie' }]} />
          <Section className="mt-6">
            <EmptyState
              icon={Server}
              title="Nie ma takiego zasobu"
              description="Zasób mógł zostać usunięty albo nie masz do niego dostępu."
            />
          </Section>
        </Page>
      );
    }
    return (
      <Page>
        <Breadcrumbs items={[{ label: 'Utrzymanie', to: '/utrzymanie' }]} />
        <Section className="mt-6">
          <ErrorState onRetry={() => void query.refetch()} retrying={query.isFetching} />
        </Section>
      </Page>
    );
  }
  return <AssetDetailContent detail={query.data} />;
}

function AssetDetailContent({ detail }: { detail: AssetDetail }) {
  const access = useAccess();
  const { asset, service, canEdit } = detail;
  useRememberRecent({
    type: 'asset',
    id: asset.id,
    title: asset.name,
    subtitle: `${ASSET_TYPE_SUBTITLES[asset.type]} · ${asset.client.name}`,
    to: `/utrzymanie/${asset.id}`,
  });
  const [dialog, setDialog] = useState<
    | { kind: 'asset' }
    | { kind: 'service' }
    | { kind: 'component'; component?: AssetComponentDto }
    | { kind: 'renewal'; renewal?: SupplierRenewalDto }
    | { kind: 'reminders' }
    | null
  >(null);
  const close = (open: boolean) => !open && setDialog(null);
  const seesRates = access.flag('seesRates');
  const canEditService = canEdit && access.can('maintenance', 'edit');
  const href = assetHref(asset.address, asset.name, asset.type);
  const meta = [
    ASSET_TYPE_SUBTITLES[asset.type].charAt(0).toUpperCase() + ASSET_TYPE_SUBTITLES[asset.type].slice(1),
    locationText(asset.provider, asset.server),
    `klient ${asset.client.name}`,
    asset.projectName ? `projekt ${asset.projectName}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const due = service?.nextPaymentDate && service.daysToPayment !== null ? service : null;
  const showBanner = due && (due.daysToPayment ?? 99) <= 30 && due.status === 'active';

  return (
    <Page>
      <Breadcrumbs
        items={[
          { label: 'Utrzymanie', to: '/utrzymanie' },
          { label: asset.client.name, to: `/utrzymanie?klient=${asset.client.id}` },
        ]}
      />
      <header className="mt-2.5 flex flex-col gap-4 md:flex-row md:items-center">
        <div className="flex min-w-0 flex-1 items-center gap-3.5">
          <AssetTypeIcon type={asset.type} size={44} />
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
              <h1 className="min-w-0 truncate text-h3 text-fg" title={asset.name}>
                {asset.name}
              </h1>
              <StatusPill tone={ASSET_STATUS_TONES[asset.status]}>{ASSET_STATUS_LABELS[asset.status]}</StatusPill>
              {asset.alarmSimulated && <StatusPill tone="warning">Symulacja</StatusPill>}
              {asset.archivedAt && <span className="text-caption text-fg-muted">w archiwum</span>}
            </div>
            <p className="text-body-s text-fg-secondary">{meta}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {href && (
            <Button asChild variant="secondary" className={TOUCH_BUTTON}>
              <a href={href} target="_blank" rel="noopener noreferrer">
                <ExternalLink size={16} strokeWidth={1.75} aria-hidden />
                Otwórz
              </a>
            </Button>
          )}
          {canEdit && (
            <Button
              variant="secondary"
              icon={Pencil}
              onClick={() => setDialog({ kind: 'asset' })}
              className={TOUCH_BUTTON}
            >
              Edytuj
            </Button>
          )}
        </div>
      </header>

      {showBanner && due && (
        <ContextBanner
          tone="warning"
          icon={RefreshCw}
          className="mt-[18px]"
          linkTo={detail.pendingClientReminder ? '/akceptacje' : undefined}
        >
          {(due.daysToPayment ?? 0) < 0
            ? `Płatność za utrzymanie ${daysLeftText(due.daysToPayment ?? 0)} (${dateText(due.nextPaymentDate as string)}).`
            : `Płatność za utrzymanie ${daysLeftText(due.daysToPayment ?? 0)} (${shortDate(due.nextPaymentDate as string)}).`}
          {detail.pendingClientReminder ? ' Przypomnienie dla klienta czeka w Akceptacjach.' : ''}
        </ContextBanner>
      )}

      <div className="mt-6 grid gap-4 md:gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-4 md:gap-6">
          <AgreementsSection detail={detail} canEdit={canEditService} onEdit={() => setDialog({ kind: 'service' })} />
          <ComponentsSection
            detail={detail}
            seesRates={seesRates}
            canEdit={canEdit}
            onAdd={() => setDialog({ kind: 'component' })}
            onOpen={(component) => setDialog({ kind: 'component', component })}
          />
          <CredentialsSection assetId={asset.id} assetName={asset.name} />
          <Section aria-label="Historia">
            <CardSectionHeader title="Historia" />
            <ObjectHistory objectType="hosted_asset" objectId={asset.id} pageSize={10} refreshKey={detail} />
          </Section>
        </div>
        <div className="flex flex-col gap-4">
          <PaymentsCard
            detail={detail}
            seesRates={seesRates}
            canEdit={canEdit}
            onAddCost={() => setDialog({ kind: 'renewal' })}
            onOpenCost={(renewal) => setDialog({ kind: 'renewal', renewal })}
          />
          <RemindersCard detail={detail} canEdit={canEdit} onEdit={() => setDialog({ kind: 'reminders' })} />
          {/* Fala 10: serwer ma kilka monitorów (porty TCP, meldunek) */}
          {asset.type === 'server' ? (
            <ServerMonitorsCard assetId={asset.id} />
          ) : (
            <AssetMonitoringCard assetId={asset.id} assetName={asset.name} />
          )}
        </div>
      </div>

      <EditAssetDialog open={dialog?.kind === 'asset'} onOpenChange={close} asset={asset} />
      <ServiceFormDialog
        open={dialog?.kind === 'service'}
        onOpenChange={close}
        clientId={asset.client.id}
        service={service ?? undefined}
        defaultKind="maintenance"
        defaultAssetIds={[asset.id]}
      />
      <ComponentDialog
        open={dialog?.kind === 'component'}
        onOpenChange={close}
        assetId={asset.id}
        component={dialog?.kind === 'component' ? dialog.component : undefined}
      />
      <SupplierRenewalDialog
        open={dialog?.kind === 'renewal'}
        onOpenChange={close}
        assetId={asset.id}
        components={detail.components}
        renewal={dialog?.kind === 'renewal' ? dialog.renewal : undefined}
      />
      <RemindersDialog
        open={dialog?.kind === 'reminders'}
        onOpenChange={close}
        rules={detail.reminderRules}
        serviceId={service?.id ?? null}
        supplierRenewals={detail.supplierRenewals}
      />
    </Page>
  );
}

/** „1 990 zł / rok” → „1 990 zł netto / rok”. */
function priceNet(price: string): string {
  return price.includes(' / ') ? price.replace(' / ', ' netto / ') : `${price} netto`;
}

/** „16.10” (bieżący rok) albo „16.10.2027”. */
function shortDate(date: string): string {
  const [y, m, d] = date.split('-');
  return y === String(new Date().getFullYear()) ? `${d}.${m}` : `${d}.${m}.${y}`;
}

// ── Lewa kolumna ────────────────────────────────────────────────────────────

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-line py-2.5 sm:min-h-[42px] sm:flex-row sm:gap-4">
      <dt className="shrink-0 text-body-s text-fg-muted sm:w-[110px]">{label}</dt>
      <dd className="min-w-0 text-body-s whitespace-pre-line text-fg">{children}</dd>
    </div>
  );
}

function AgreementsSection({ detail, canEdit, onEdit }: { detail: AssetDetail; canEdit: boolean; onEdit: () => void }) {
  const { service } = detail;
  const access = useAccess();
  if (detail.serviceHidden) {
    // Tryb częściowy klienta: ustalenia tylko do odczytu, bez ceny, warunków i płatności (DECISIONS [P] Fala 1).
    const agreements = detail.agreements;
    return (
      <Section aria-label="Ustalenia">
        <CardSectionHeader title="Ustalenia" />
        {agreements && (
          <dl>
            <InfoRow label="Zakres">
              {agreements.descriptionMd?.trim() || <span className="text-fg-muted">—</span>}
            </InfoRow>
            {agreements.hoursPoolSeconds && (
              <InfoRow label="Pula godzin">{`${Math.round((agreements.hoursPoolSeconds / 3600) * 10) / 10} h`}</InfoRow>
            )}
            {agreements.periodStart && <InfoRow label="Od">{formatDate(agreements.periodStart)}</InfoRow>}
          </dl>
        )}
        <p className="border-b border-line py-3 text-body-s text-fg-muted">
          {agreements
            ? 'Umowa, kwoty i płatności klienta są poza Twoim zakresem.'
            : 'Umowa klienta jest poza Twoim zakresem.'}
        </p>
      </Section>
    );
  }
  if (!service) {
    return (
      <Section aria-label="Ustalenia">
        <CardSectionHeader title="Ustalenia" />
        <EmptyState
          compact
          title="Brak umowy utrzymania"
          description="Dodaj umowę, żeby system pilnował płatności i przypomnień."
          action={
            canEdit ? (
              <Button variant="ghost" size="s" className={TOUCH_BUTTON} onClick={onEdit}>
                Dodaj umowę
              </Button>
            ) : undefined
          }
        />
      </Section>
    );
  }
  const price = servicePriceText(service);
  const since = serviceSinceText(service);
  return (
    <Section aria-label="Ustalenia">
      <CardSectionHeader
        title="Ustalenia"
        action={
          <div className="flex items-center gap-3">
            {service.sourceKnowledgeId && <span className="text-caption text-fg-muted">źródło w Wiedzy</span>}
            {canEdit && <SectionAction onClick={onEdit}>Zmień</SectionAction>}
          </div>
        }
      />
      <dl>
        <InfoRow label="Zakres">{service.descriptionMd?.trim() || <span className="text-fg-muted">—</span>}</InfoRow>
        {access.flag('seesRates') && (
          <InfoRow label="Cena">{price ? priceNet(price) : <span className="text-fg-muted">—</span>}</InfoRow>
        )}
        {service.billingTerms && <InfoRow label="Warunki">{service.billingTerms}</InfoRow>}
        {service.hoursPoolSeconds && (
          <InfoRow label="Pula godzin">
            {service.pool ? (
              // wykorzystanie w bieżącym miesiącu (17.10.2026): „1 h 20 min z 3 h · październik · zostało 1 h 40 min”
              <span className="flex flex-col gap-1.5">
                <span>
                  {poolUsageLine(service.pool)} · <PoolBalance usage={service.pool} />
                </span>
                <PoolProgress usage={service.pool} label="Pula godzin" className="max-w-[240px]" />
              </span>
            ) : (
              `${Math.round((service.hoursPoolSeconds / 3600) * 10) / 10} h`
            )}
          </InfoRow>
        )}
        {since && <InfoRow label="Od">{since}</InfoRow>}
      </dl>
    </Section>
  );
}

function componentTitle(c: AssetComponentDto, assetName: string): string {
  if (c.kind === 'domain') return `Domena ${assetName}`;
  return ASSET_COMPONENT_KIND_LABELS[c.kind];
}

function ComponentsSection({
  detail,
  seesRates,
  canEdit,
  onAdd,
  onOpen,
}: {
  detail: AssetDetail;
  seesRates: boolean;
  canEdit: boolean;
  onAdd: () => void;
  onOpen: (c: AssetComponentDto) => void;
}) {
  const { components, supplierRenewals, asset } = detail;
  return (
    <Section aria-label="Elementy">
      <CardSectionHeader
        title="Elementy"
        action={canEdit ? <SectionAction onClick={onAdd}>Dodaj element</SectionAction> : undefined}
      />
      {components.length === 0 ? (
        <EmptyState compact title="Brak elementów" description="Hosting, domena, certyfikat SSL, baza danych." />
      ) : (
        <ul>
          {components.map((c) => {
            const Icon = COMPONENT_ICONS[c.kind];
            const title = componentTitle(c, asset.name);
            const secondary = c.description && c.description !== title ? c.description : c.provider;
            const cost = supplierRenewals.find((s) => s.componentId === c.id);
            let right: string;
            if (cost) {
              right = [
                seesRates && cost.amount ? formatMoneyAuto(cost.amount, cost.currency) : null,
                dateText(cost.renewsOn),
              ]
                .filter(Boolean)
                .join(' · ');
            } else if (c.expiresOn) right = `do ${dateText(c.expiresOn)}`;
            else right = c.included ? 'w pakiecie' : 'płatny osobno';
            const row = (
              <>
                <Icon size={15} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
                <span className="min-w-0 truncate text-body-s font-medium text-fg">{title}</span>
                {secondary && (
                  <span className="hidden min-w-0 truncate text-caption text-fg-muted sm:inline">{secondary}</span>
                )}
                <span className="flex-1" />
                <span className="shrink-0 text-caption whitespace-nowrap text-fg-secondary">{right}</span>
              </>
            );
            const cls = 'flex min-h-11 w-full items-center gap-3 border-b border-line py-2 text-left';
            return (
              <li key={c.id}>
                {canEdit ? (
                  <button
                    type="button"
                    onClick={() => onOpen(c)}
                    className={cn(
                      cls,
                      'transition-colors hover:bg-surface focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
                    )}
                  >
                    {row}
                  </button>
                ) : (
                  <div className={cls}>{row}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

// ── Prawa kolumna ───────────────────────────────────────────────────────────

function Card({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <Section aria-label={title} className="md:px-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-caption text-fg-muted">{title}</h2>
        {action}
      </div>
      <div className="mt-2.5">{children}</div>
    </Section>
  );
}

function SubHeader({ children }: { children: ReactNode }) {
  return <h3 className="mt-3 border-t border-line pt-2.5 text-caption text-fg-muted">{children}</h3>;
}

function PaymentsCard({
  detail,
  seesRates,
  canEdit,
  onAddCost,
  onOpenCost,
}: {
  detail: AssetDetail;
  seesRates: boolean;
  canEdit: boolean;
  onAddCost: () => void;
  onOpenCost: (renewal: SupplierRenewalDto) => void;
}) {
  const { service, supplierRenewals, components, payments } = detail;
  const access = useAccess();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const generate = trpc.maintenance.generatePayment.useMutation();
  const invoiced = trpc.maintenance.markPaymentInvoiced.useMutation();
  const paid = trpc.maintenance.markPaymentPaid.useMutation();
  const prepare = trpc.billing.fromRenewalPayment.useMutation();
  const canMoney = canEdit && seesRates;
  // faktura za odnowienie: przez Rozliczenia, zawsze po akceptacji (MODULE_UTRZYMANIE §3 p. 4)
  const canPrepare = seesRates && access.can('billing', 'edit') && access.flag('preparesBilling');
  const usesBilling = access.can('billing');
  const hostingIncluded = components.filter((c) => c.kind === 'hosting' && c.included);
  const currentPayment = service?.nextPaymentDate
    ? payments.find((p) => p.periodStart === service.nextPaymentDate)
    : undefined;

  const run = async (fn: () => Promise<unknown>, message: string) => {
    try {
      await fn();
      toast.success(message);
      await Promise.all([utils.maintenance.invalidate(), utils.services.invalidate()]);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const tone =
    service?.daysToPayment != null
      ? renewalTone({ daysLeft: service.daysToPayment, overdue: service.daysToPayment < 0 })
      : 'neutral';

  return (
    <Card title="Klient płaci">
      {service ? (
        <>
          {seesRates && service.price ? (
            <p className="text-h4 font-semibold text-fg tabular">
              {amountWithCycle(service.price, service.cycle, service.currency)}
            </p>
          ) : (
            <p className="text-body-s font-medium text-fg">{service.name}</p>
          )}
          {service.nextPaymentDate && (
            <p className="mt-1.5 flex items-center gap-1.5 text-caption text-fg-secondary">
              {tone !== 'neutral' && <Dot tone={tone} size={6} />}
              następna {dateText(service.nextPaymentDate)}
              {service.daysToPayment !== null && `, ${daysLeftText(service.daysToPayment)}`}
            </p>
          )}
        </>
      ) : (
        <p className="text-caption text-fg-muted">
          {detail.serviceHidden ? 'Umowa poza Twoim zakresem.' : 'Brak umowy utrzymania.'}
        </p>
      )}

      <SubHeader>My płacimy</SubHeader>
      <ul className="mt-2 flex flex-col gap-2">
        {supplierRenewals.map((s) => {
          const label = `${s.notes ?? s.provider} · ${dateText(s.renewsOn)}`;
          const content = (
            <>
              <span className="min-w-0 flex-1 truncate text-left text-caption text-fg-secondary">{label}</span>
              {seesRates && s.amount && (
                <Mono className="text-caption text-fg">{formatMoneyAuto(s.amount, s.currency)}</Mono>
              )}
            </>
          );
          return (
            <li key={s.id}>
              {canMoney ? (
                <button
                  type="button"
                  onClick={() => onOpenCost(s)}
                  className="flex w-full items-center gap-2 rounded-xs hover:underline focus-visible:outline-2 focus-visible:outline-accent"
                >
                  {content}
                </button>
              ) : (
                <div className="flex items-center gap-2">{content}</div>
              )}
            </li>
          );
        })}
        {hostingIncluded.map((c) => (
          <li key={c.id} className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-caption text-fg-secondary">
              {ASSET_COMPONENT_KIND_LABELS[c.kind]}
            </span>
            <span className="text-caption text-fg-secondary">w pakiecie</span>
          </li>
        ))}
        {supplierRenewals.length === 0 && hostingIncluded.length === 0 && (
          <li className="text-caption text-fg-muted">Brak kosztów dostawców.</li>
        )}
      </ul>
      {canMoney && (
        <button
          type="button"
          onClick={onAddCost}
          className={`mt-2 rounded-xs text-caption font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`}
        >
          Dodaj koszt dostawcy
        </button>
      )}

      {service && (
        <>
          <SubHeader>Historia</SubHeader>
          <ul className="mt-2 flex flex-col gap-2">
            {payments.length === 0 && <li className="text-caption text-fg-muted">Brak płatności.</li>}
            {payments.map((p) => (
              <PaymentRow
                key={p.id}
                payment={p}
                seesRates={seesRates}
                canEdit={canMoney}
                busy={invoiced.isPending || paid.isPending || prepare.isPending}
                canPrepare={canPrepare}
                usesBilling={usesBilling}
                onPrepare={async () => {
                  try {
                    const draft = await prepare.mutateAsync({ paymentId: p.id });
                    await Promise.all([utils.maintenance.invalidate(), utils.billing.invalidate()]);
                    toast.success(`Przygotowano zestawienie ${draft.label}`, {
                      description: 'Sprawdź je i wyślij do wystawienia faktury.',
                    });
                    navigate(`/rozliczenia/${draft.id}`);
                  } catch (err) {
                    toast.error(errorMessage(err));
                  }
                }}
                onInvoiced={() =>
                  run(() => invoiced.mutateAsync({ id: p.id, version: p.version }), 'Oznaczono jako zafakturowaną')
                }
                onPaid={() =>
                  run(() => paid.mutateAsync({ id: p.id, version: p.version }), 'Opłacona — termin przesunięty o cykl')
                }
              />
            ))}
          </ul>
          {canMoney && service.nextPaymentDate && !currentPayment && (
            <Button
              variant="secondary"
              fullWidth
              className="mt-3 max-md:h-11"
              loading={generate.isPending}
              onClick={() =>
                run(() => generate.mutateAsync({ serviceId: service.id }), 'Przygotowano płatność za odnowienie')
              }
            >
              Przygotuj płatność za odnowienie
            </Button>
          )}
        </>
      )}
    </Card>
  );
}

/**
 * Płatność odnowienia: z modułem Rozliczenia faktura idzie przez zestawienie („Przygotuj zestawienie” → akceptacja
 * „Wystawia faktury” → opłacenie na zestawieniu przenosi się tutaj i przesuwa termin umowy). Płatność w zestawieniu
 * nie ma ręcznych „Zafakturowana” / „Opłacona” (backend też je odrzuca); bez modułu Rozliczenia — oznaczenie ręczne.
 */
function PaymentRow({
  payment: p,
  seesRates,
  canEdit,
  busy,
  canPrepare,
  usesBilling,
  onPrepare,
  onInvoiced,
  onPaid,
}: {
  payment: RenewalPaymentDto;
  seesRates: boolean;
  canEdit: boolean;
  busy: boolean;
  canPrepare: boolean;
  usesBilling: boolean;
  onPrepare: () => void;
  onInvoiced: () => void;
  onPaid: () => void;
}) {
  const tone = RENEWAL_PAYMENT_STATUS_TONES[p.effectiveStatus];
  const inDraft = p.billingDraftId !== null;
  const open = p.status === 'pending' || p.status === 'overdue';
  return (
    <li className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-caption text-fg-secondary">
          {dateText(p.periodStart)}
          {seesRates && p.amount ? ` · ${formatMoneyAuto(p.amount, p.currency)}` : ''}
        </span>
        <StatusPill tone={tone} size="s">
          {RENEWAL_PAYMENT_STATUS_LABELS[p.effectiveStatus]}
        </StatusPill>
      </div>
      {inDraft && p.status !== 'paid' && (
        <p className="text-caption text-fg-muted">
          {usesBilling ? (
            <Link
              to={`/rozliczenia/${p.billingDraftId}`}
              className="rounded-xs text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
            >
              W zestawieniu — faktura i opłacenie w Rozliczeniach
            </Link>
          ) : (
            'W zestawieniu — faktura i opłacenie w Rozliczeniach'
          )}
        </p>
      )}
      {canEdit && p.status !== 'paid' && !inDraft && (
        <div className="flex flex-wrap gap-1.5">
          {canPrepare && open && (
            <Button variant="secondary" size="s" className={TOUCH_BUTTON} onClick={onPrepare} disabled={busy}>
              Przygotuj zestawienie
            </Button>
          )}
          {!usesBilling && p.status !== 'invoiced' && (
            <Button variant="ghost" size="s" className={TOUCH_BUTTON} onClick={onInvoiced} disabled={busy}>
              Zafakturowana
            </Button>
          )}
          <Button variant="ghost" size="s" className={TOUCH_BUTTON} icon={Check} onClick={onPaid} disabled={busy}>
            Opłacona
          </Button>
        </div>
      )}
    </li>
  );
}

function RemindersCard({ detail, canEdit, onEdit }: { detail: AssetDetail; canEdit: boolean; onEdit: () => void }) {
  const rules = detail.reminderRules;
  const supplierName = (id: string | null) => {
    const s = detail.supplierRenewals.find((x) => x.id === id);
    return s ? (s.notes ?? s.provider) : null;
  };
  return (
    <Card title="Przypomnienia" action={canEdit ? <SectionAction onClick={onEdit}>Zmień</SectionAction> : undefined}>
      {rules.length === 0 ? (
        <p className="text-caption text-fg-muted">Brak przypomnień.</p>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {rules.map((r) => (
            <li
              key={r.id}
              className={cn('flex items-start gap-2 text-caption', r.enabled ? 'text-fg' : 'text-fg-muted')}
            >
              {r.enabled ? (
                <Check size={14} strokeWidth={2} aria-hidden className="mt-px shrink-0 text-success" />
              ) : (
                <Minus size={14} strokeWidth={2} aria-hidden className="mt-px shrink-0" />
              )}
              <span>
                {r.targetType === 'supplier_renewal' && supplierName(r.supplierRenewalId)
                  ? `${supplierName(r.supplierRenewalId)}: `
                  : ''}
                {describeReminderRule(r)}
                {!r.enabled && ' (wyłączone)'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
